import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  Eye,
  EyeOff,
  FlaskConical,
  LayoutDashboard,
  Lock,
  PenTool,
  Radar,
  Swords,
  TableProperties,
  User,
} from "lucide-react";

const AUTH_STORAGE_KEY = "fsrov_auth";
const FIXED_USERNAME = "fsrov1";
const FIXED_PASSWORD = "fsflyhigh";

const FEATURES = [
  { icon: LayoutDashboard, title: "ภาพรวมทีม", text: "ผลแข่ง ฟอร์ม คู่แข่ง ฮีโร่ที่ใช้" },
  { icon: Activity, title: "Team Performance", text: "เทียบซ้อมกับแข่งจริงรายฮีโร่" },
  { icon: TableProperties, title: "Draft Recap", text: "แบน/พิกทุกเกม ส่งออกได้" },
  { icon: Swords, title: "Draft Simulator", text: "จำลองดราฟต์พร้อมสถิติ" },
  { icon: PenTool, title: "Sucrose Tactic", text: "วางแผนบนแมพ อนิเมชัน" },
  { icon: Radar, title: "Minimap Lab", text: "แกงก์ เส้นทาง ไฟต์จากคลิป" },
  { icon: FlaskConical, title: "Meta Lab", text: "ฮีโร่ ไอเทม คำนวณดาเมจ" },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const signedIn = localStorage.getItem(AUTH_STORAGE_KEY) === "true";

  function handleSubmit(e) {
    e.preventDefault();
    const user = username.trim();
    const pass = password.trim();

    if (user === FIXED_USERNAME && pass === FIXED_PASSWORD) {
      localStorage.setItem(AUTH_STORAGE_KEY, "true");
      setErrorMessage("");
      navigate("/upload");
      return;
    }

    setErrorMessage("ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง");
  }

  function handleForgotPassword() {
    window.alert("ลืมรหัสผ่าน: กรุณาติดต่อผู้ดูแลระบบของทีม");
  }

  return (
    <div className="login-root">
      <section className="login-brand" aria-hidden="false">
        <div className="login-brand-glow" />
        <div className="login-brand-inner">
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img
              src={`${process.env.PUBLIC_URL}/Full sense.jpg`}
              alt="Full Sense"
              onError={(e) => {
                e.currentTarget.src = `${process.env.PUBLIC_URL}/full-sense-logo.jpg`;
              }}
              style={{ width: 54, height: 54, borderRadius: 14, objectFit: "cover", boxShadow: "0 0 0 1px rgba(255,107,26,0.45)" }}
            />
            <div>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 20, letterSpacing: "0.04em" }}>FULL SENSE</div>
              <div style={{ color: "var(--text-3)", fontSize: 12.5, letterSpacing: "0.06em" }}>ROV ESPORTS · ANALYTICS</div>
            </div>
          </div>

          <h1 className="login-headline">
            ข้อมูลทั้งทีม
            <br />
            <span>ในที่เดียว</span>
          </h1>
          <p style={{ color: "var(--text-2)", fontSize: 15, maxWidth: 440, margin: 0, lineHeight: 1.7 }}>
            สครีม ดราฟต์ ฮีโร่ และแผนการเล่น — อ่านจากไฟล์บันทึกของทีมโดยตรง สำหรับโค้ช นักวิเคราะห์ และผู้เล่น
          </p>

          <div className="login-features">
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <div key={title} className="login-feature">
                <div className="login-feature-icon">
                  <Icon size={16} strokeWidth={2.2} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{title}</div>
                  <div style={{ fontSize: 12, color: "var(--text-3)" }}>{text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="login-form-side">
        <form onSubmit={handleSubmit} className="login-card ds-fade-in" noValidate>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>เข้าสู่ระบบ</h2>
            <p style={{ margin: "6px 0 0", color: "var(--text-3)", fontSize: 13 }}>ใช้บัญชีของทีม Full Sense</p>
          </div>

          <label className="ds-field">
            <span className="ds-label">ชื่อผู้ใช้</span>
            <div className="ds-input-wrap">
              <User size={15} />
              <input
                className="ds-input"
                style={{ height: 42, fontSize: 14 }}
                type="text"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  if (errorMessage) setErrorMessage("");
                }}
                placeholder="กรอกชื่อผู้ใช้"
              />
            </div>
          </label>

          <label className="ds-field">
            <span className="ds-label">รหัสผ่าน</span>
            <div className="ds-input-wrap">
              <Lock size={15} />
              <input
                className="ds-input"
                style={{ height: 42, fontSize: 14, paddingRight: 42 }}
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errorMessage) setErrorMessage("");
                }}
                placeholder="กรอกรหัสผ่าน"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}
                style={{
                  position: "absolute",
                  right: 6,
                  width: 32,
                  height: 32,
                  display: "grid",
                  placeItems: "center",
                  border: 0,
                  borderRadius: 8,
                  background: "transparent",
                  color: "var(--text-3)",
                  cursor: "pointer",
                }}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </label>

          {errorMessage && (
            <div role="alert" className="ds-callout" style={{ borderColor: "rgba(239,77,90,0.35)", background: "var(--loss-soft)", color: "#ffb3b9" }}>
              {errorMessage}
            </div>
          )}

          <button type="submit" className="ds-btn ds-btn--primary ds-btn--lg" style={{ width: "100%" }}>
            เข้าสู่ระบบ <ArrowRight size={16} />
          </button>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <button type="button" onClick={handleForgotPassword} className="ds-btn ds-btn--ghost ds-btn--sm" style={{ paddingLeft: 0 }}>
              ลืมรหัสผ่าน?
            </button>
            {signedIn ? (
              <button type="button" onClick={() => navigate("/dashboard")} className="ds-btn ds-btn--soft ds-btn--sm">
                เข้าระบบอยู่แล้ว · ไปต่อ <ArrowRight size={13} />
              </button>
            ) : null}
          </div>

          <div className="login-powered">
            <span />
            <img src={`${process.env.PUBLIC_URL}/ctrlg.png`} alt="ctrlg" style={{ width: 18, height: 18, objectFit: "contain" }} />
            powered by ctrlg
            <span />
          </div>
        </form>
      </section>

      <style>{`
        .login-root { min-height: 100vh; display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(380px, 0.85fr); background: var(--bg); color: var(--text); }
        .login-brand { position: relative; overflow: hidden; border-right: 1px solid var(--border);
          background:
            radial-gradient(900px 520px at 10% -10%, rgba(255,107,26,0.20), transparent 60%),
            radial-gradient(700px 500px at 110% 120%, rgba(255,107,26,0.10), transparent 60%),
            linear-gradient(180deg, #0d1016, #0a0c10); }
        .login-brand::after { content: ""; position: absolute; inset: 0; pointer-events: none; opacity: .5;
          background-image: linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
          background-size: 48px 48px; mask-image: radial-gradient(ellipse at 30% 30%, #000 30%, transparent 75%); }
        .login-brand-inner { position: relative; z-index: 1; height: 100%; display: flex; flex-direction: column; justify-content: center; gap: 26px; padding: 56px clamp(28px, 6vw, 88px); max-width: 760px; }
        .login-headline { margin: 0; font-family: var(--font-display); font-size: clamp(34px, 4.4vw, 56px); line-height: 1.05; font-weight: 700; letter-spacing: -0.01em; }
        .login-headline span { color: var(--accent); }
        .login-features { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 6px; }
        .login-feature { display: flex; gap: 11px; align-items: flex-start; padding: 11px 12px; border-radius: 12px; background: rgba(255,255,255,0.025); border: 1px solid var(--border); }
        .login-feature-icon { width: 30px; height: 30px; border-radius: 8px; display: grid; place-items: center; flex: none; background: var(--accent-soft); color: var(--accent); }
        .login-form-side { display: grid; place-items: center; padding: 32px 20px; }
        .login-card { width: 100%; max-width: 380px; display: grid; gap: 16px; }
        .login-powered { display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 10px; color: var(--text-3); font-size: 12px; }
        .login-powered span { width: 36px; height: 1px; background: var(--border-2); }
        @media (max-width: 980px) {
          .login-root { grid-template-columns: 1fr; }
          .login-brand { border-right: 0; border-bottom: 1px solid var(--border); }
          .login-brand-inner { padding: 32px 20px; gap: 18px; }
          .login-features { display: none; }
        }
      `}</style>
    </div>
  );
}
